import { formatMoney } from '@theme/money-formatting';

/**
 * Casuki — página de producto.
 *
 * <casuki-product> resuelve variantes, cambia la galería y controla la cantidad.
 * Los precios llegan ya formateados desde Liquid (filtro `money`), así que el
 * navegador nunca tiene que adivinar el formato de moneda de la tienda.
 */

class CasukiProduct extends HTMLElement {
  connectedCallback() {
    const dataNode = this.querySelector('[data-casuki-variant-data]');
    this.variants = dataNode ? JSON.parse(dataNode.textContent) : [];

    this.initGallery();
    this.initGalleryNav();
    this.initQuantity();
    this.initVariants();
    this.initInCartLine();
    this.initShippingBar();
  }

  disconnectedCallback() {
    if (this.cartStore && this.onCartChange) {
      this.cartStore.removeEventListener('casuki:cart-change', this.onCartChange);
    }
    const store = window.CasukiCart && window.CasukiCart.cartStore;
    if (store && this.onShipCartChange) {
      store.removeEventListener('casuki:cart-change', this.onShipCartChange);
    }
  }

  /* --- "Ya tienes N en el carrito" ---------------------------------------- */

  initInCartLine() {
    this.inCartLine = this.querySelector('[data-casuki-in-cart]');
    if (!this.inCartLine) return;

    this.cartStore = window.CasukiCart && window.CasukiCart.cartStore;
    if (!this.cartStore) return;

    this.onCartChange = () => this.renderInCartLine();
    this.cartStore.addEventListener('casuki:cart-change', this.onCartChange);
    this.renderInCartLine();
  }

  /** @param {string} [variantId] - si no se pasa, usa la variante actualmente seleccionada. */
  renderInCartLine(variantId) {
    if (!this.inCartLine || !this.cartStore) return;

    const id = variantId || this.inCartLine.dataset.casukiInCart;
    this.inCartLine.dataset.casukiInCart = id;

    const quantity = this.cartStore.quantityFor(id);
    this.inCartLine.hidden = quantity === 0;

    const countNode = this.inCartLine.querySelector('[data-casuki-in-cart-count]');
    if (countNode) countNode.textContent = String(quantity);
  }

  /* --- Galería ----------------------------------------------------------- */

  initGallery() {
    this.stage = this.querySelector('[data-casuki-stage]');
    this.slides = Array.from(this.querySelectorAll('[data-casuki-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-casuki-thumb]'));

    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
    }

    if (this.stage && this.stage.dataset.zoom === 'true') {
      this.stage.addEventListener('mousemove', (event) => {
        const rect = this.stage.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        const active = this.stage.querySelector('.casuki-gallery__slide.is-active img');
        if (active) active.style.transformOrigin = `${x}% ${y}%`;
      });

      this.stage.addEventListener('mouseleave', () => {
        const active = this.stage.querySelector('.casuki-gallery__slide.is-active img');
        if (active) active.style.transformOrigin = 'center center';
      });
    }
  }

  showMedia(mediaId) {
    if (!mediaId) return;

    for (const slide of this.slides) {
      slide.classList.toggle('is-active', slide.dataset.mediaId === mediaId);
    }
    for (const thumb of this.thumbs) {
      thumb.setAttribute('aria-current', String(thumb.dataset.mediaId === mediaId));
    }

    this.syncGalleryCounter();
  }

  /* --- Pasar entre las fotos del producto -------------------------------- */

  /**
   * Las flechas y el gesto de deslizar recorren las fotos de este producto.
   * Da la vuelta en los extremos: de la última se llega a la primera, así
   * nunca se choca contra un botón que no responde.
   */
  initGalleryNav() {
    this.galleryZone = this.querySelector('[data-casuki-gallery-zone]');
    if (!this.galleryZone || this.slides.length < 2) return;

    this.galleryCounter = this.querySelector('[data-casuki-gallery-index]');

    for (const boton of this.querySelectorAll('[data-casuki-gallery-nav]')) {
      boton.addEventListener('click', () => {
        this.stepMedia(boton.dataset.casukiGalleryNav === 'next' ? 1 : -1);
      });
    }

    // Flechas del teclado, cuando el foco está sobre la galería.
    this.galleryZone.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.stepMedia(1);
      else if (event.key === 'ArrowLeft') this.stepMedia(-1);
      else return;
      event.preventDefault();
    });

    let inicioX = 0;
    let inicioY = 0;
    let siguiendo = false;

    this.galleryZone.addEventListener(
      'touchstart',
      (event) => {
        siguiendo = event.touches.length === 1;
        if (!siguiendo) return;
        inicioX = event.touches[0].clientX;
        inicioY = event.touches[0].clientY;
      },
      { passive: true }
    );

    this.galleryZone.addEventListener(
      'touchend',
      (event) => {
        if (!siguiendo) return;
        siguiendo = false;

        const toque = event.changedTouches[0];
        const dx = toque.clientX - inicioX;
        const dy = toque.clientY - inicioY;

        // Solo cuenta si el gesto es largo y claramente horizontal: si no,
        // sería imposible scrollear la página con el dedo sobre la foto.
        if (Math.abs(dx) < 45) return;
        if (Math.abs(dx) < Math.abs(dy) * 1.6) return;

        this.stepMedia(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );

    this.syncGalleryCounter();
  }

  /** @param {number} paso - 1 para avanzar, -1 para retroceder. */
  stepMedia(paso) {
    if (this.slides.length < 2) return;
    const actual = this.slides.findIndex((s) => s.classList.contains('is-active'));
    const desde = actual < 0 ? 0 : actual;
    const destino = (desde + paso + this.slides.length) % this.slides.length;
    this.showMedia(this.slides[destino].dataset.mediaId);
  }

  syncGalleryCounter() {
    if (!this.galleryCounter) return;
    const actual = this.slides.findIndex((s) => s.classList.contains('is-active'));
    this.galleryCounter.textContent = String(actual < 0 ? 1 : actual + 1);
  }

  /* --- Cantidad ---------------------------------------------------------- */

  initQuantity() {
    this.qtyInput = this.querySelector('[data-casuki-qty-input]');
    if (!this.qtyInput) return;

    for (const button of this.querySelectorAll('[data-casuki-qty-step]')) {
      button.addEventListener('click', () => {
        const step = Number(button.dataset.casukiQtyStep);
        const next = Math.max(1, Number(this.qtyInput.value || 1) + step);
        this.qtyInput.value = String(next);
        this.syncQuantity();
      });
    }

    this.qtyInput.addEventListener('change', () => {
      const value = Math.max(1, Math.floor(Number(this.qtyInput.value) || 1));
      this.qtyInput.value = String(value);
      this.syncQuantity();
    });

    this.syncQuantity();
  }

  syncQuantity() {
    const value = Number(this.qtyInput.value || 1);

    const minus = this.querySelector('[data-casuki-qty-step="-1"]');
    if (minus) minus.disabled = value <= 1;

    // El input del formulario de Shopify es otro nodo: hay que mantenerlo al día.
    const formQty = this.querySelector('[data-casuki-form-qty]');
    if (formQty) formQty.value = String(value);

    this.syncShippingBar();
  }

  /* --- Variantes --------------------------------------------------------- */

  initVariants() {
    this.swatches = Array.from(this.querySelectorAll('[data-casuki-swatch]'));
    if (this.swatches.length === 0) return;

    this.optionCount = Number(this.dataset.optionCount || 1);
    this.selected = [];

    for (const swatch of this.swatches) {
      if (swatch.getAttribute('aria-pressed') === 'true') {
        this.selected[Number(swatch.dataset.optionIndex)] = swatch.dataset.value;
      }

      swatch.addEventListener('click', () => {
        const index = Number(swatch.dataset.optionIndex);
        this.selected[index] = swatch.dataset.value;

        for (const other of this.swatches) {
          if (Number(other.dataset.optionIndex) !== index) continue;
          other.setAttribute('aria-pressed', String(other === swatch));
        }

        const label = this.querySelector(`[data-casuki-option-value="${index}"]`);
        if (label) label.textContent = swatch.dataset.value;

        this.resolveVariant();
      });
    }

    this.markUnavailable();
  }

  findVariant(options) {
    return this.variants.find((variant) =>
      variant.options.every((value, index) => value === options[index])
    );
  }

  resolveVariant() {
    const variant = this.findVariant(this.selected);
    this.markUnavailable();

    const priceNode = this.querySelector('[data-casuki-price]');
    const compareNode = this.querySelector('[data-casuki-compare]');
    const saveNode = this.querySelector('[data-casuki-save]');
    const stockNode = this.querySelector('[data-casuki-stock]');
    const skuNode = this.querySelector('[data-casuki-sku]');
    const variantInput = this.querySelector('[data-casuki-variant-input]');
    const addButton = this.querySelector('[data-casuki-add] button, [data-casuki-add] .button');

    if (!variant) {
      if (stockNode) {
        stockNode.textContent = 'Esa combinación no existe';
        stockNode.setAttribute('data-out', '');
      }
      if (addButton) addButton.disabled = true;
      return;
    }

    if (variantInput) variantInput.value = String(variant.id);

    if (priceNode && priceNode.textContent.trim() !== variant.price) {
      priceNode.textContent = variant.price;
      priceNode.parentElement.classList.remove('is-updating');
      void priceNode.parentElement.offsetWidth; // reinicia la animación
      priceNode.parentElement.classList.add('is-updating');
    }

    if (compareNode) {
      compareNode.textContent = variant.hasCompare ? variant.compareAt : '';
      compareNode.hidden = !variant.hasCompare;
    }

    if (saveNode) {
      saveNode.textContent = variant.savePercent ? `-${variant.savePercent}%` : '';
      saveNode.hidden = !variant.savePercent;
    }

    if (stockNode) {
      stockNode.textContent = variant.available ? 'Disponible' : 'Agotado';
      stockNode.toggleAttribute('data-out', !variant.available);
    }

    if (skuNode) {
      skuNode.textContent = variant.sku ? `SKU ${variant.sku}` : '';
      skuNode.hidden = !variant.sku;
    }

    if (addButton) {
      addButton.disabled = !variant.available;
      const label = addButton.querySelector('.add-to-cart-text') || addButton;
      label.textContent = variant.available ? this.dataset.addLabel : 'Agotado';
    }

    if (variant.mediaId) this.showMedia(String(variant.mediaId));

    this.renderInCartLine(String(variant.id));
    this.syncShippingBar();

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  /* --- Barra de envío gratis, proyectada -------------------------------- */

  /**
   * En la página de producto la barra no puede leer solo el carrito: lo que
   * el cliente quiere saber es si *lo que está eligiendo* le da el envío
   * gratis. Se proyecta carrito + cantidad × precio de la variante y se
   * repinta al cambiar cantidad, pack, variante o carrito. No toca el
   * carrito: eso dispararía AddToCart al píxel en cada clic del selector.
   */
  initShippingBar() {
    this.shipBar = this.querySelector('[data-casuki-ship-bar]');
    if (!this.shipBar) return;

    const data = this.shipBar.dataset;
    this.shipThreshold = Number(data.casukiShipThreshold) || 0;
    this.shipCartTotal = Number(data.casukiShipCart) || 0;
    this.shipFormat = data.casukiMoneyFormat || '{{amount}}';
    this.shipCurrency = data.casukiCurrency || 'COP';
    this.shipTextRemaining = data.casukiShipTextRemaining || '';
    this.shipTextDone = data.casukiShipTextDone || '';
    // Si el carrito ya aporta, hay que decirlo: si no, "tienes envío gratis" con una
    // sola unidad parece un error.
    this.shipTextRemainingCart = data.casukiShipTextRemainingCart || this.shipTextRemaining;
    this.shipTextDoneCart = data.casukiShipTextDoneCart || this.shipTextDone;
    if (this.shipThreshold <= 0) return;

    // El selector de packs escribe el input oculto aunque no haya contador visible.
    const formQty = this.querySelector('[data-casuki-form-qty]');
    if (formQty) formQty.addEventListener('change', () => this.syncShippingBar());

    // Si el carrito cambia (drawer, otra pestaña), cambia el punto de partida.
    const store = window.CasukiCart && window.CasukiCart.cartStore;
    if (store) {
      this.onShipCartChange = () => {
        if (typeof store.totalPrice === 'number') this.shipCartTotal = store.totalPrice;
        this.syncShippingBar();
      };
      store.addEventListener('casuki:cart-change', this.onShipCartChange);
    }

    this.syncShippingBar();
  }

  currentQuantity() {
    const formQty = this.querySelector('[data-casuki-form-qty]');
    const raw = formQty ? formQty.value : this.qtyInput ? this.qtyInput.value : 1;
    return Math.max(1, Math.floor(Number(raw) || 1));
  }

  currentUnitPrice() {
    const variantInput = this.querySelector('[data-casuki-variant-input]');
    const id = variantInput ? String(variantInput.value) : '';
    const variant = this.variants.find((v) => String(v.id) === id) || this.variants[0];
    return variant ? Number(variant.priceCents) || 0 : 0;
  }

  syncShippingBar() {
    if (!this.shipBar || !(this.shipThreshold > 0)) return;

    const projected = this.shipCartTotal + this.currentQuantity() * this.currentUnitPrice();
    const remaining = Math.max(0, this.shipThreshold - projected);
    const progress = Math.min(100, Math.round((projected * 100) / this.shipThreshold));

    const text = this.shipBar.querySelector('[data-casuki-ship-text]');
    if (text) {
      const cartHelps = this.shipCartTotal > 0;
      if (remaining > 0) {
        const amount = document.createElement('strong');
        amount.textContent = formatMoney(remaining, this.shipFormat, this.shipCurrency);
        const [before, after] = (cartHelps ? this.shipTextRemainingCart : this.shipTextRemaining).split('{amount}');
        text.replaceChildren(before || '', amount, after || '');
      } else {
        text.replaceChildren(cartHelps ? this.shipTextDoneCart : this.shipTextDone);
      }
    }

    const fill = this.shipBar.querySelector('[data-casuki-ship-fill]');
    if (fill) fill.style.width = progress + '%';
  }


  /**
   * Tacha los valores que, combinados con lo ya elegido, no dan una variante
   * comprable. Se sigue pudiendo hacer clic: elegirlos reajusta el resto.
   */
  markUnavailable() {
    for (const swatch of this.swatches) {
      const index = Number(swatch.dataset.optionIndex);
      const probe = [...this.selected];
      probe[index] = swatch.dataset.value;

      const match = this.variants.find((variant) =>
        variant.options.every((value, i) => (i === index ? value === probe[i] : probe[i] == null || value === probe[i]))
      );

      swatch.dataset.unavailable = String(!match || !match.available);
    }
  }
}

if (!customElements.get('casuki-product')) {
  customElements.define('casuki-product', CasukiProduct);
}

/* --- Productos relacionados ----------------------------------------------- */

/**
 * Trae recomendaciones con la API nativa de Shopify. Devuelve la sección ya
 * renderizada en Liquid, así que las tarjetas salen idénticas a las del catálogo.
 */
class CasukiRelated extends HTMLElement {
  connectedCallback() {
    const { productId, sectionId, limit, intent } = this.dataset;
    if (!productId || !sectionId) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit || 4}&intent=${intent || 'related'}`;

    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('casuki-related');
        // Sin recomendaciones reales se conserva lo ya pintado (misma colección).
        if (!fresh || fresh.children.length === 0) return;

        this.innerHTML = fresh.innerHTML;
        this.removeAttribute('hidden');
      })
      .catch(() => {
        // Si falla la red, el respaldo del render inicial sigue en pantalla.
      });
  }
}

if (!customElements.get('casuki-related')) {
  customElements.define('casuki-related', CasukiRelated);
}

/* --- "Paga ya" -------------------------------------------------------------- */

/**
 * Sustituye al botón exprés de PayPal, cuyo texto no es editable desde el
 * tema. Agrega al carrito y salta directo al checkout; PayPal sigue
 * disponible ahí como método de pago.
 */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-casuki-pay-now]');
  if (!button || button.disabled) return;

  const form = button.closest('form[data-type="add-to-cart-form"]');
  if (!form) return;

  const label = button.querySelector('[data-casuki-pay-label]');
  const original = label.textContent;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  button.disabled = true;
  label.textContent = 'Un momento…';

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error('add-to-cart failed');

    window.location.href = `${root}checkout`;
  } catch {
    // Si falla, devolvemos el botón a su estado para que se pueda reintentar.
    button.disabled = false;
    label.textContent = original;
  }
});
